import { useEffect, useRef, useState } from "react";
import { Image, Typography } from "antd";

interface ChristmasConfig {
  avatar?: string;
  greetings_content?: string;
  name?: string;
  background?: string;
  message?: string;
  video?: string;
  list_image?: string[];
}

interface Props {
  config: ChristmasConfig;
}

/* ─── Snowfall Effect ─── */
const Snowfall = () => {
  const [flakes] = useState(() =>
    Array.from({ length: 30 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 10,
      duration: 6 + Math.random() * 8,
      size: 8 + Math.random() * 14,
      opacity: 0.3 + Math.random() * 0.6,
      swing: Math.random() > 0.5 ? "xmas-swing1" : "xmas-swing2",
    })),
  );

  return (
    <>
      {flakes.map((f) => (
        <div
          key={f.id}
          className="fixed pointer-events-none z-50 text-white"
          style={{
            left: `${f.left}%`,
            fontSize: `${f.size}px`,
            opacity: f.opacity,
            animation: `xmas-fall ${f.duration}s linear ${f.delay}s infinite, ${f.swing} ${f.duration * 0.5}s ease-in-out ${f.delay}s infinite`,
          }}
        >
          ❄
        </div>
      ))}
    </>
  );
};

/* ─── Animated Section (scroll reveal) ─── */
const AnimatedSection = ({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setTimeout(() => setVisible(true), delay);
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        transition: "opacity 0.8s ease, transform 0.8s ease",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(40px)",
      }}
    >
      {children}
    </div>
  );
};

/* ─── Ornament Divider ─── */
const OrnamentDivider = () => (
  <div className="flex items-center justify-center gap-2 py-6">
    <div className="h-px w-10" style={{ background: "linear-gradient(to right, transparent, rgba(255,215,0,0.5))" }} />
    <span className="text-xl" style={{ animation: "xmas-pulse 2s ease-in-out infinite" }}>
      🎄
    </span>
    <div className="h-px w-14" style={{ background: "rgba(255,215,0,0.4)" }} />
    <span className="text-sm" style={{ animation: "xmas-pulse 2s ease-in-out 0.3s infinite" }}>
      ⭐
    </span>
    <div className="h-px w-14" style={{ background: "rgba(255,215,0,0.4)" }} />
    <span className="text-xl" style={{ animation: "xmas-pulse 2s ease-in-out 0.6s infinite" }}>
      🎄
    </span>
    <div className="h-px w-10" style={{ background: "linear-gradient(to left, transparent, rgba(255,215,0,0.5))" }} />
  </div>
);

/* ═══════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════ */
const PublicChristmas = ({ config }: Props) => {
  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ background: "#0a0f1a" }}>
      {/* Global Christmas Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Great+Vibes&family=Playfair+Display:wght@400;700&family=Cormorant+Garamond:ital,wght@0,300;0,500;1,300&display=swap');

        @keyframes xmas-fall {
          0% { transform: translateY(-10vh) rotate(0deg); }
          100% { transform: translateY(105vh) rotate(360deg); }
        }
        @keyframes xmas-swing1 {
          0%, 100% { margin-left: 0; }
          50% { margin-left: 25px; }
        }
        @keyframes xmas-swing2 {
          0%, 100% { margin-left: 0; }
          50% { margin-left: -25px; }
        }
        @keyframes xmas-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        @keyframes xmas-glow {
          0%, 100% { filter: brightness(1) drop-shadow(0 0 6px rgba(255,215,0,0.3)); }
          50% { filter: brightness(1.2) drop-shadow(0 0 18px rgba(255,215,0,0.6)); }
        }
        @keyframes xmas-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes xmas-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        @keyframes xmas-twinkle {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
        @keyframes xmas-scroll-dot {
          0% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(8px); }
        }

        .xmas-shimmer-text {
          background: linear-gradient(90deg, #ffd700 0%, #fff8dc 25%, #ffd700 50%, #fff8dc 75%, #ffd700 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: xmas-shimmer 3s linear infinite;
        }

        .xmas-glass {
          background: linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.08);
        }

        .xmas-hero-overlay {
          background: linear-gradient(180deg, transparent 0%, rgba(10,15,26,0.3) 40%, rgba(10,15,26,0.85) 75%, #0a0f1a 100%);
        }

        .xmas-stars-bg {
          background-image:
            radial-gradient(2px 2px at 20px 30px, rgba(255,255,255,0.3), transparent),
            radial-gradient(2px 2px at 40px 70px, rgba(255,255,255,0.2), transparent),
            radial-gradient(1px 1px at 90px 40px, rgba(255,255,255,0.4), transparent),
            radial-gradient(1px 1px at 130px 80px, rgba(255,255,255,0.2), transparent),
            radial-gradient(2px 2px at 160px 30px, rgba(255,255,255,0.3), transparent);
          background-size: 200px 100px;
        }

        .xmas-gallery-img {
          transition: transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94), box-shadow 0.4s ease;
        }
        .xmas-gallery-img:hover {
          transform: scale(1.03);
          box-shadow: 0 16px 50px rgba(0,0,0,0.5);
        }

        .xmas-container ::-webkit-scrollbar { width: 0; }
      `}</style>

      <Snowfall />

      {/* ══════════ HERO SECTION ══════════ */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          {config.background ? (
            <img src={config.background} alt="" className="w-full h-full object-cover" />
          ) : (
            <div
              className="w-full h-full"
              style={{ background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)" }}
            />
          )}
          <div className="xmas-hero-overlay absolute inset-0" />
          <div className="absolute inset-0" style={{ background: "rgba(10,15,26,0.4)" }} />
        </div>

        {/* Stars */}
        <div className="absolute inset-0 xmas-stars-bg opacity-60" />

        {/* Hanging Ornaments */}
        <div className="absolute top-0 left-6 flex flex-col items-center">
          <div
            className="w-px h-16"
            style={{ background: "linear-gradient(to bottom, rgba(255,215,0,0.6), transparent)" }}
          />
          <span
            className="text-3xl"
            style={{
              animation: "xmas-float 3s ease-in-out infinite",
              filter: "drop-shadow(0 4px 15px rgba(255,68,68,0.3))",
            }}
          >
            🔴
          </span>
        </div>
        <div className="absolute top-0 right-10 flex flex-col items-center">
          <div
            className="w-px h-24"
            style={{ background: "linear-gradient(to bottom, rgba(255,215,0,0.6), transparent)" }}
          />
          <span
            className="text-2xl"
            style={{
              animation: "xmas-float 3.5s ease-in-out 0.5s infinite",
              filter: "drop-shadow(0 4px 15px rgba(255,215,0,0.3))",
            }}
          >
            🟡
          </span>
        </div>
        <div className="absolute top-0 left-1/3 flex flex-col items-center">
          <div
            className="w-px h-12"
            style={{ background: "linear-gradient(to bottom, rgba(255,215,0,0.6), transparent)" }}
          />
          <span
            className="text-2xl"
            style={{
              animation: "xmas-float 4s ease-in-out 1s infinite",
              filter: "drop-shadow(0 4px 15px rgba(0,200,0,0.3))",
            }}
          >
            🟢
          </span>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 py-20">
          <div className="mb-8" style={{ animation: "xmas-float 4s ease-in-out infinite" }}>
            <span className="text-6xl" style={{ animation: "xmas-glow 2s ease-in-out infinite" }}>
              ⭐
            </span>
          </div>

          <h1
            className="xmas-shimmer-text leading-tight mb-4"
            style={{ fontFamily: "'Great Vibes', cursive", fontSize: "clamp(2.8rem, 10vw, 3.5rem)" }}
          >
            Merry Christmas
          </h1>

          <div className="flex items-center justify-center gap-2 mb-6">
            <span style={{ color: "rgba(255,215,0,0.6)" }}>✦</span>
            <span
              className="tracking-widest uppercase"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: "1rem", color: "rgba(255,255,255,0.7)" }}
            >
              & Happy New Year
            </span>
            <span style={{ color: "rgba(255,215,0,0.6)" }}>✦</span>
          </div>

          {config.greetings_content && (
            <p
              className="max-w-xs mx-auto leading-relaxed"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "1.2rem",
                color: "rgba(255,255,255,0.6)",
                fontStyle: "italic",
              }}
            >
              {config.greetings_content}
            </p>
          )}

          {/* Scroll indicator */}
          <div className="mt-16 flex flex-col items-center gap-2">
            <span
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "0.75rem",
                letterSpacing: "0.15em",
                color: "rgba(255,255,255,0.4)",
              }}
            >
              SCROLL DOWN
            </span>
            <div
              className="flex items-start justify-center"
              style={{
                width: 20,
                height: 32,
                borderRadius: 10,
                border: "2px solid rgba(255,255,255,0.25)",
                padding: 4,
              }}
            >
              <div
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.7)",
                  animation: "xmas-scroll-dot 1.5s ease-in-out infinite",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ SENDER PROFILE + MESSAGE ══════════ */}
      <section className="relative py-16 px-4">
        <div className="absolute inset-0 xmas-stars-bg opacity-30" />

        <AnimatedSection className="flex flex-col items-center text-center">
          {/* Avatar */}
          {config.avatar && (
            <div className="relative mb-6">
              <div
                className="rounded-full p-1"
                style={{
                  background: "linear-gradient(135deg, rgba(255,215,0,0.4), rgba(255,68,68,0.4), rgba(0,200,0,0.4))",
                }}
              >
                <Image
                  src={config.avatar}
                  alt="avatar"
                  width={120}
                  height={120}
                  preview={false}
                  className="rounded-full object-cover"
                  style={{ border: "3px solid #0a0f1a", display: "block" }}
                />
              </div>
              {/* Decorative ring */}
              <div
                className="absolute rounded-full"
                style={{
                  inset: -10,
                  border: "1px solid rgba(255,215,0,0.2)",
                  animation: "xmas-pulse 3s ease-in-out infinite",
                }}
              />
              <span
                className="absolute text-2xl"
                style={{ bottom: -4, right: -4, animation: "xmas-float 2s ease-in-out infinite" }}
              >
                🎅
              </span>
            </div>
          )}

          {config.name && (
            <>
              <span
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  color: "rgba(255,255,255,0.45)",
                  fontSize: "1rem",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                }}
              >
                From
              </span>
              <Typography.Title
                level={2}
                style={{ color: "#fff", marginBottom: 4, marginTop: 4, fontFamily: "'Playfair Display', serif" }}
              >
                {config.name}
              </Typography.Title>
              <div
                className="mt-2"
                style={{
                  width: 60,
                  height: 2,
                  background: "linear-gradient(to right, transparent, rgba(255,215,0,0.6), transparent)",
                }}
              />
            </>
          )}
        </AnimatedSection>

        <OrnamentDivider />

        {/* Message Card */}
        {config.message && (
          <AnimatedSection delay={200}>
            <div className="xmas-glass rounded-3xl p-6 max-w-md mx-auto relative overflow-hidden">
              {/* Corner candles */}
              <span className="absolute top-3 left-3 text-base opacity-40">🕯️</span>
              <span className="absolute top-3 right-3 text-base opacity-40">🕯️</span>

              {/* Top border glow */}
              <div
                className="absolute top-0 left-0 w-full h-px"
                style={{ background: "linear-gradient(to right, transparent, rgba(255,215,0,0.3), transparent)" }}
              />

              <Typography.Paragraph
                className="text-center"
                style={{
                  color: "rgba(255,255,255,0.8)",
                  marginBottom: 0,
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "1.15rem",
                  lineHeight: 1.8,
                  fontStyle: "italic",
                }}
              >
                "{config.message}"
              </Typography.Paragraph>

              {/* Bottom border glow */}
              <div
                className="absolute bottom-0 left-0 w-full h-px"
                style={{ background: "linear-gradient(to right, transparent, rgba(255,215,0,0.3), transparent)" }}
              />
            </div>
          </AnimatedSection>
        )}
      </section>

      {/* ══════════ VIDEO ══════════ */}
      {config.video && (
        <section className="relative py-12 px-4">
          <AnimatedSection>
            <div className="text-center mb-6">
              <span
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  color: "rgba(255,255,255,0.4)",
                  fontSize: "0.8rem",
                  letterSpacing: "0.15em",
                  textTransform: "uppercase",
                }}
              >
                A Special Moment
              </span>
              <Typography.Title
                level={4}
                style={{ color: "#fff", marginTop: 4, fontFamily: "'Playfair Display', serif" }}
              >
                Video Chúc Mừng 🎬
              </Typography.Title>
            </div>

            <div className="xmas-glass rounded-3xl overflow-hidden max-w-md mx-auto">
              <video
                src={config.video}
                controls
                playsInline
                className="w-full"
                style={{ aspectRatio: "16/9", objectFit: "cover" }}
                poster={config.background}
              />
            </div>
          </AnimatedSection>
        </section>
      )}

      {/* ══════════ IMAGE GALLERY ══════════ */}
      {config.list_image && config.list_image.length > 0 && (
        <section className="relative py-12 px-4">
          <div className="absolute inset-0 xmas-stars-bg opacity-20" />

          <AnimatedSection className="text-center mb-8">
            <span
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                color: "rgba(255,255,255,0.4)",
                fontSize: "0.8rem",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
              }}
            >
              Memories
            </span>
            <Typography.Title
              level={3}
              style={{ color: "#fff", marginTop: 4, fontFamily: "'Playfair Display', serif" }}
            >
              Khoảnh Khắc Đáng Nhớ
            </Typography.Title>
            <div className="flex items-center justify-center gap-2 mt-2">
              <div className="h-px w-8" style={{ background: "rgba(255,215,0,0.4)" }} />
              <span style={{ color: "rgba(255,215,0,0.6)", fontSize: "0.7rem" }}>✦</span>
              <div className="h-px w-8" style={{ background: "rgba(255,215,0,0.4)" }} />
            </div>
          </AnimatedSection>

          {/* Masonry Gallery */}
          <div className="max-w-md mx-auto columns-2 gap-3" style={{ columnGap: 10 }}>
            {config.list_image.map((img, i) => (
              <AnimatedSection key={i} delay={i * 120} className="mb-3 break-inside-avoid">
                <div
                  className="xmas-gallery-img rounded-2xl overflow-hidden"
                  style={{ border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <Image
                    src={img}
                    alt={`memory-${i}`}
                    className="w-full object-cover"
                    style={{
                      aspectRatio: i % 3 === 0 ? "3/4" : i % 3 === 1 ? "1/1" : "4/5",
                      display: "block",
                    }}
                  />
                </div>
              </AnimatedSection>
            ))}
          </div>
        </section>
      )}

      {/* ══════════ WISHES / CTA ══════════ */}
      <section className="relative py-20 px-4">
        <AnimatedSection className="text-center">
          <div className="mb-6 flex items-center justify-center gap-3">
            <span className="text-3xl" style={{ animation: "xmas-float 3s ease-in-out infinite" }}>
              🎁
            </span>
            <span className="text-5xl" style={{ animation: "xmas-float 3s ease-in-out 0.3s infinite" }}>
              🎄
            </span>
            <span className="text-3xl" style={{ animation: "xmas-float 3s ease-in-out 0.6s infinite" }}>
              🎁
            </span>
          </div>

          <h3
            className="xmas-shimmer-text mb-5"
            style={{ fontFamily: "'Great Vibes', cursive", fontSize: "clamp(2.2rem, 8vw, 3rem)" }}
          >
            Chúc Giáng Sinh Vui Vẻ!
          </h3>

          <p
            className="max-w-xs mx-auto leading-relaxed mb-10"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.1rem", color: "rgba(255,255,255,0.45)" }}
          >
            Mong rằng mùa lễ này sẽ mang đến cho bạn thật nhiều niềm vui, hạnh phúc và bình an.
          </p>

          {/* Decorative text tree */}
          <div
            className="mb-10 select-none"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: "rgba(255,255,255,0.15)",
              fontSize: "0.85rem",
              lineHeight: 1.6,
            }}
          >
            <div>✦</div>
            <div>✦ ✦ ✦</div>
            <div>✦ ✦ ✦ ✦ ✦</div>
            <div>✦ ✦ ✦ ✦ ✦ ✦ ✦</div>
            <div>✦ ✦ ✦ ✦ ✦ ✦ ✦ ✦ ✦</div>
            <div>✦ ✦ ✦</div>
          </div>

          {/* <button
            className="px-10 py-3 rounded-full tracking-wider uppercase text-sm text-white"
            style={{
              fontFamily: "'Playfair Display', serif",
              background: "linear-gradient(to right, rgba(200,30,30,0.8), rgba(160,20,20,0.8))",
              border: "1px solid rgba(255,100,100,0.3)",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.05)";
              e.currentTarget.style.boxShadow = "0 8px 30px rgba(200,30,30,0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            ❤️ Gửi Lời Chúc Lại
          </button> */}
        </AnimatedSection>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <footer className="relative py-8 px-4 text-center" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <Typography.Text style={{ color: "rgba(255,255,255,0.2)", fontSize: "0.8rem" }}>
          Made with ❤️ • Merry Christmas 2025
        </Typography.Text>
        <div className="mt-2 flex items-center justify-center gap-1">
          {["❄", "🎄", "⭐", "🎄", "❄"].map((e, i) => (
            <span
              key={i}
              className="text-xs"
              style={{ opacity: 0.3, animation: `xmas-twinkle 2s ease-in-out ${i * 0.3}s infinite` }}
            >
              {e}
            </span>
          ))}
        </div>
      </footer>
    </div>
  );
};

export default PublicChristmas;
